/**
 * FloatingPhone — small button bottom-left that expands into a unified
 * panel with: dialer, text input, recent calls/sms, and a "Click to call".
 *
 * Hidden on /pay/* and /portal/* (public customer pages).
 *
 * Twilio call masking note: the actual phone provider (Twilio / VoIP) must be
 * wired in once the user provides their account credentials. Today this panel
 * logs everything to /api/calls and /api/sms (already-existing endpoints) and
 * surfaces the office's masked number from settings so the customer always
 * sees the same number regardless of which tech is calling.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Phone,
  X,
  Send,
  PhoneCall,
  MessageSquare,
  Mic,
  Delete,
  Shield,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../api/client';
import { telephonyAPI } from '../api/client';

const PUBLIC_PREFIXES = ['/pay/', '/portal/', '/login', '/register'];

export default function FloatingPhone() {
  const location = useLocation();
  const isPublic = PUBLIC_PREFIXES.some((p) => location.pathname.startsWith(p));
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('call'); // call | mask | sms | recent
  const [dialedNumber, setDialedNumber] = useState('');
  const [smsTo, setSmsTo] = useState('');
  const [smsBody, setSmsBody] = useState('');
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const [telStatus, setTelStatus] = useState(null);
  const [maskTechPhone, setMaskTechPhone] = useState('');
  const [maskCustomerPhone, setMaskCustomerPhone] = useState('');
  const [activeMaskSession, setActiveMaskSession] = useState(null);

  const loadRecent = async () => {
    try {
      const [calls, sms] = await Promise.all([
        apiClient.get('/api/calls?limit=10').then((r) => r.data),
        apiClient.get('/api/sms?limit=10').then((r) => r.data),
      ]);
      const merged = [
        ...(calls || []).map((c) => ({
          ...c,
          _kind: 'call',
          _ts: c.started_at || c.created_at,
        })),
        ...(sms || []).map((s) => ({
          ...s,
          _kind: 'sms',
          _ts: s.sent_at || s.created_at,
        })),
      ].sort((a, b) => String(b._ts).localeCompare(String(a._ts)));
      setRecent(merged.slice(0, 12));
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    if (open && mode === 'recent') loadRecent();
  }, [open, mode]);

  useEffect(() => {
    if (open && !telStatus) {
      telephonyAPI.status().then(setTelStatus).catch(() => {});
    }
  }, [open, telStatus]);

  if (isPublic) return null;

  const press = (k) => setDialedNumber((n) => (n + k).slice(0, 20));
  const back = () => setDialedNumber((n) => n.slice(0, -1));

  const placeCall = async () => {
    if (!dialedNumber) return toast.error('Enter a number');
    setBusy(true);
    try {
      // Logs the call attempt to /api/calls (Twilio bridge to be wired later).
      const { data } = await apiClient.post('/api/calls', {
        direction: 'outgoing',
        from_number: 'office',
        to_number: dialedNumber,
        status: 'incoming',
        notes: 'Initiated from floating phone',
      });
      toast.success(`Call logged · ${data.to_number}`);
      // Open the device's native dialer as a courtesy
      window.location.href = `tel:${dialedNumber}`;
      setDialedNumber('');
    } catch (e) {
      toast.error('Could not place call');
    } finally {
      setBusy(false);
    }
  };

  const sendSms = async () => {
    if (!smsTo || !smsBody) return toast.error('To & message required');
    setBusy(true);
    try {
      await apiClient.post('/api/sms', {
        direction: 'outgoing',
        from_number: 'office',
        to_number: smsTo,
        body: smsBody,
      });
      toast.success('SMS logged');
      setSmsBody('');
    } catch (e) {
      toast.error('Could not send SMS');
    } finally {
      setBusy(false);
    }
  };

  const startMaskedCall = async () => {
    if (!maskTechPhone || !maskCustomerPhone) {
      return toast.error('Tech & customer phone required');
    }
    setBusy(true);
    try {
      const result = await telephonyAPI.maskCall({
        tech_phone: maskTechPhone,
        customer_phone: maskCustomerPhone,
      });
      setActiveMaskSession(result);
      toast.success(
        result.mode === 'demo'
          ? `DEMO call routed via ${result.proxy_number}`
          : `Masked call connecting via ${result.proxy_number}`,
      );
      // In demo: open tech's device dialer with customer's number so they can hear ringing
      if (result.mode === 'demo' && result.tech_dials) {
        window.location.href = `tel:${result.tech_dials}`;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start masked call');
    } finally {
      setBusy(false);
    }
  };

  const endMaskedCall = async () => {
    if (!activeMaskSession) return;
    setBusy(true);
    try {
      await telephonyAPI.endCall(activeMaskSession.session_id, {
        duration_seconds: 0,
        outcome: 'completed',
      });
      toast.success('Call ended');
      setActiveMaskSession(null);
      setMaskTechPhone('');
      setMaskCustomerPhone('');
    } catch (e) {
      toast.error('Could not end session');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Trigger button — top right of header */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed z-[60] h-10 w-10 rounded-full bg-emerald-500 text-white shadow-lg hover:bg-emerald-600 flex items-center justify-center transition active:scale-95"
          style={{
            top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
            right: 'calc(env(safe-area-inset-right, 0px) + 12px)',
          }}
          data-testid="floating-phone-button"
          aria-label="Open phone"
        >
          <Phone className="h-5 w-5" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed z-[60] w-80 max-w-[calc(100vw-1.5rem)] rounded-2xl bg-card border border-emerald-500/40 shadow-2xl overflow-hidden"
          style={{
            top: 'calc(env(safe-area-inset-top, 0px) + 64px)',
            right: 'calc(env(safe-area-inset-right, 0px) + 12px)',
            maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 100px)',
          }}
          data-testid="floating-phone-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b bg-emerald-500/10">
            <div className="flex items-center gap-2 font-semibold text-emerald-500">
              <Phone className="h-4 w-4" /> Phone
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded hover:bg-muted"
              data-testid="floating-phone-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Tabs */}
          <div className="grid grid-cols-4 border-b text-xs">
            {[
              { id: 'call', label: 'Dialer', icon: PhoneCall },
              { id: 'mask', label: 'Mask', icon: Shield },
              { id: 'sms', label: 'Text', icon: MessageSquare },
              { id: 'recent', label: 'Recent', icon: Mic },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setMode(id)}
                className={`py-2 flex items-center justify-center gap-1 ${
                  mode === id
                    ? 'text-emerald-500 border-b-2 border-emerald-500'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`floating-phone-tab-${id}`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>

          {/* Body */}
          <div className="p-4">
            {telStatus && (
              <div
                className={`mb-3 px-2.5 py-1.5 rounded text-[11px] font-medium flex items-center gap-1.5 ${
                  telStatus.mode === 'demo'
                    ? 'bg-violet-500/10 text-violet-400 border border-violet-500/30'
                    : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                }`}
                data-testid="floating-phone-mode-badge"
              >
                <Sparkles className="h-3 w-3" />
                {telStatus.mode === 'demo' ? 'DEMO' : 'LIVE'} · {telStatus.office_number}
              </div>
            )}
            {mode === 'call' && (
              <div>
                <input
                  type="tel"
                  value={dialedNumber}
                  onChange={(e) => setDialedNumber(e.target.value.replace(/[^\d+()\- ]/g, ''))}
                  placeholder="Enter number"
                  className="w-full text-center text-2xl tracking-widest font-mono py-2 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="floating-phone-dial-input"
                />
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((k) => (
                    <button
                      key={k}
                      onClick={() => press(k)}
                      className="py-3 rounded-lg bg-muted/60 hover:bg-muted text-lg font-bold transition active:scale-95"
                      data-testid={`floating-phone-key-${k}`}
                    >
                      {k}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <button
                    onClick={back}
                    className="py-2.5 rounded-lg bg-muted/60 hover:bg-muted text-sm font-semibold flex items-center justify-center gap-1"
                  >
                    <Delete className="h-4 w-4" /> Back
                  </button>
                  <button
                    onClick={placeCall}
                    disabled={busy || !dialedNumber}
                    className="py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-1"
                    data-testid="floating-phone-call-button"
                  >
                    <PhoneCall className="h-4 w-4" /> Call
                  </button>
                </div>
              </div>
            )}

            {mode === 'mask' && (
              <div className="space-y-3" data-testid="floating-phone-mask-panel">
                {activeMaskSession ? (
                  <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
                    <div className="text-[11px] uppercase tracking-wider text-emerald-400 font-bold">
                      Session active
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Customer sees: <span className="font-mono text-foreground">{activeMaskSession.customer_sees}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Mode: <b className="text-foreground uppercase">{activeMaskSession.mode}</b>
                    </div>
                    {activeMaskSession.demo_notice && (
                      <div className="text-[11px] text-violet-300 mt-2 leading-snug">
                        {activeMaskSession.demo_notice}
                      </div>
                    )}
                    <button
                      onClick={endMaskedCall}
                      disabled={busy}
                      className="mt-3 w-full py-2 rounded-lg bg-rose-500/90 hover:bg-rose-500 text-white text-sm font-bold flex items-center justify-center gap-1"
                      data-testid="floating-phone-mask-end"
                    >
                      End Call
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-[11px] text-muted-foreground leading-snug">
                      Privacy bridge: customer & tech only ever see the office number.
                    </p>
                    <input
                      type="tel"
                      value={maskTechPhone}
                      onChange={(e) => setMaskTechPhone(e.target.value)}
                      placeholder="Tech phone (e.g. +15551234567)"
                      className="w-full py-2 px-3 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      data-testid="floating-phone-mask-tech"
                    />
                    <input
                      type="tel"
                      value={maskCustomerPhone}
                      onChange={(e) => setMaskCustomerPhone(e.target.value)}
                      placeholder="Customer phone"
                      className="w-full py-2 px-3 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      data-testid="floating-phone-mask-customer"
                    />
                    <button
                      onClick={startMaskedCall}
                      disabled={busy || !maskTechPhone || !maskCustomerPhone}
                      className="w-full py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-1"
                      data-testid="floating-phone-mask-start"
                    >
                      <Shield className="h-4 w-4" /> Start Masked Call
                    </button>
                  </>
                )}
              </div>
            )}

            {mode === 'sms' && (
              <div className="space-y-3">
                <input
                  type="tel"
                  value={smsTo}
                  onChange={(e) => setSmsTo(e.target.value)}
                  placeholder="To (phone)"
                  className="w-full py-2 px-3 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="floating-phone-sms-to"
                />
                <textarea
                  rows={4}
                  value={smsBody}
                  onChange={(e) => setSmsBody(e.target.value)}
                  placeholder="Message..."
                  className="w-full py-2 px-3 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                  data-testid="floating-phone-sms-body"
                />
                <button
                  onClick={sendSms}
                  disabled={busy || !smsTo || !smsBody}
                  className="w-full py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-1"
                  data-testid="floating-phone-sms-send"
                >
                  <Send className="h-4 w-4" /> Send
                </button>
              </div>
            )}

            {mode === 'recent' && (
              <div className="max-h-72 overflow-y-auto -mx-2">
                {recent.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6">No recent activity.</p>
                ) : (
                  <ul className="divide-y" data-testid="floating-phone-recent-list">
                    {recent.map((r) => (
                      <li key={`${r._kind}-${r.id}`} className="px-2 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          {r._kind === 'call' ? (
                            <PhoneCall className="h-3.5 w-3.5 text-cyan-500" />
                          ) : (
                            <MessageSquare className="h-3.5 w-3.5 text-violet-500" />
                          )}
                          <span className="font-mono text-xs">
                            {r.to_number || r.from_number}
                          </span>
                          <span className="text-[10px] text-muted-foreground ml-auto">
                            {(r._ts || '').slice(11, 16)}
                          </span>
                        </div>
                        {r.body && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{r.body}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
